import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { FREE_MODE } from '../config/appConfig';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { MessageSquare, Send, ArrowLeft, Loader2, User, Building2, Wifi, WifiOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { trackContactCompany } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Convert HTTP URL to WebSocket URL
// WebSocket must go through /api prefix for ingress routing
const getWsUrl = () => {
  const url = new URL(BACKEND_URL);
  const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${url.host}`;
};

const Chat = () => {
  const { user, token, isAuthenticated } = useAuth();
  const { fetchUnreadCount, playNotificationSound } = useChat();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  
  // Refs
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const selectedConversationRef = useRef(null);
  const connectFnRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  
  // Keep ref in sync with state for WebSocket callbacks
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);
  
  // Check for URL params (start conversation with specific user/offer)
  const otherUserId = searchParams.get('user');
  const offerId = searchParams.get('offer');
  
  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Handle incoming WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'conversations':
        setConversations(data.conversations || []);
        break;
        
      case 'conversation_started':
        setSelectedConversation(data.conversation);
        setMessages([]);
        setConversations(prev => {
          const exists = prev.find(c => c.id === data.conversation.id);
          if (!exists) {
            return [data.conversation, ...prev];
          }
          return prev;
        });
        break;
        
      case 'messages':
        setMessages(data.messages || []);
        break;
        
      case 'new_message':
        const currentConv = selectedConversationRef.current;
        if (currentConv?.id === data.conversation_id) {
          setMessages(prev => [...prev, data.message]);
          // Mark as read immediately if we're viewing this conversation
          if (data.message.sender_id !== user?.user_id) {
            wsRef.current?.send(JSON.stringify({
              action: 'mark_read',
              conversation_id: data.conversation_id
            }));
          }
        } else if (data.message.sender_id !== user?.user_id) {
          // Play notification sound for messages in other conversations
          playNotificationSound?.();
        }
        setConversations(prev => {
          return prev.map(conv => {
            if (conv.id === data.conversation_id) {
              return {
                ...conv,
                last_message: data.message,
                unread_count: data.message.sender_id !== user?.user_id && currentConv?.id !== data.conversation_id
                  ? (conv.unread_count || 0) + 1 
                  : conv.unread_count
              };
            }
            return conv;
          }).sort((a, b) => {
            const aTime = a.last_message?.created_at || a.updated_at;
            const bTime = b.last_message?.created_at || b.updated_at;
            return new Date(bTime) - new Date(aTime);
          });
        });
        // Refresh global unread count
        fetchUnreadCount?.();
        break;
        
      case 'messages_read':
        setConversations(prev => {
          return prev.map(conv => {
            if (conv.id === data.conversation_id) {
              return { ...conv, unread_count: 0 };
            }
            return conv;
          });
        });
        fetchUnreadCount?.();
        break;
        
      case 'typing':
        const typingConv = selectedConversationRef.current;
        if (typingConv?.id === data.conversation_id && data.user_id !== user?.user_id) {
          if (data.is_typing) {
            setTypingUser(data.user_name || 'Użytkownik');
            // Clear typing indicator after 3 seconds
            if (typingTimeoutRef.current) {
              clearTimeout(typingTimeoutRef.current);
            }
            typingTimeoutRef.current = setTimeout(() => {
              setTypingUser(null);
            }, 3000);
          } else {
            setTypingUser(null);
          }
        }
        break;
        
      case 'error':
        toast.error(data.message);
        break;
        
      default:
        console.log('Unknown message type:', data.type);
    }
  }, [user, fetchUnreadCount, playNotificationSound]);
  
  // Connect to WebSocket - stored in ref for reconnect
  useEffect(() => {
    connectFnRef.current = () => {
      if (!isAuthenticated || !token || !user) return;
      
      // W trybie darmowym firmy mogą korzystać z czatu bez subskrypcji
      if (!FREE_MODE && user.user_type === 'company' && !user.subscription_active) {
        setSubscriptionError(true);
        setLoading(false);
        return;
      }
      
      // WebSocket URL with /api prefix for ingress routing
      const wsUrl = `${getWsUrl()}/api/ws/chat/${user.user_id}?token=${token}`;
      
      try {
        wsRef.current = new WebSocket(wsUrl);
        
        wsRef.current.onopen = () => {
          setWsConnected(true);
          setLoading(false);
          wsRef.current.send(JSON.stringify({ action: 'get_conversations' }));
          
          if (otherUserId) {
            wsRef.current.send(JSON.stringify({
              action: 'start_conversation',
              other_user_id: otherUserId,
              conversation_type: offerId ? 'offer' : 'direct',
              offer_id: offerId || undefined
            }));
          }
        };
        
        wsRef.current.onclose = (event) => {
          setWsConnected(false);
          
          if (event.code === 4003) {
            setSubscriptionError(true);
            toast.error('Aktywna subskrypcja wymagana do korzystania z czatu');
            return;
          }
          
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isAuthenticated && connectFnRef.current) {
              connectFnRef.current();
            }
          }, 3000);
        };
        
        wsRef.current.onerror = () => {
          setWsConnected(false);
        };
        
        wsRef.current.onmessage = (event) => {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        };
        
      } catch (error) {
        console.error('WebSocket connection error:', error);
        setLoading(false);
      }
    };
  }, [isAuthenticated, token, user, otherUserId, offerId, handleWebSocketMessage]);
  
  // Initialize WebSocket connection
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Small delay to ensure connectFnRef is set
    const timer = setTimeout(() => {
      if (connectFnRef.current) {
        connectFnRef.current();
      }
    }, 100);
    
    return () => {
      clearTimeout(timer);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [isAuthenticated, navigate]);
  
  // Select conversation
  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    setMessages([]);
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'get_messages',
        conversation_id: conv.id
      }));
      
      if (conv.unread_count > 0) {
        wsRef.current.send(JSON.stringify({
          action: 'mark_read',
          conversation_id: conv.id
        }));
        // Update global unread count
        fetchUnreadCount?.();
      }
    }
  };
  
  // Send typing indicator
  const sendTypingIndicator = (isTyping) => {
    if (!selectedConversation || !wsConnected || !wsRef.current) return;
    
    // Throttle typing indicators - max once per second
    const now = Date.now();
    if (isTyping && now - lastTypingSentRef.current < 1000) return;
    lastTypingSentRef.current = now;
    
    wsRef.current.send(JSON.stringify({
      action: 'typing',
      conversation_id: selectedConversation.id,
      is_typing: isTyping
    }));
  };
  
  // Handle message input change
  const handleMessageChange = (e) => {
    setNewMessage(e.target.value);
    if (e.target.value.trim()) {
      sendTypingIndicator(true);
    }
  };
  
  // Send message
  const sendMessage = () => {
    if (!newMessage.trim() || !selectedConversation || !wsConnected) return;
    
    setSending(true);
    sendTypingIndicator(false);
    
    wsRef.current.send(JSON.stringify({
      action: 'send_message',
      conversation_id: selectedConversation.id,
      content: newMessage.trim()
    }));
    
    // Śledź konwersję kontaktu w Google Ads (tylko pierwsza wiadomość w konwersacji)
    if (messages.length === 0 && selectedConversation.other_user?.user_id) {
      trackContactCompany(selectedConversation.other_user.user_id);
    }
    
    setNewMessage('');
    setSending(false);
  };
  
  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  // Clear typing indicator when conversation changes
  useEffect(() => {
    setTypingUser(null);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  }, [selectedConversation?.id]);
  
  // Subscription required view for companies
  if (subscriptionError) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="chat-subscription-required">
        <div className="bg-slate-900 py-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="font-heading font-bold text-2xl text-white">Czat</h1>
            <p className="text-slate-400 mt-1">Komunikacja w czasie rzeczywistym</p>
          </div>
        </div>
        
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="text-center">
            <CardContent className="py-12">
              <AlertCircle className="w-16 h-16 mx-auto mb-4 text-orange-500" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Subskrypcja wymagana</h2>
              <p className="text-slate-600 mb-6">
                Aby korzystać z czatu, potrzebujesz aktywnej subskrypcji firmowej.
              </p>
              <Button 
                onClick={() => navigate('/subscription')}
                className="bg-orange-500 hover:bg-orange-600"
              >
                Wykup subskrypcję
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="chat-page">
      <div className="bg-slate-900 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading font-bold text-2xl text-white">Czat</h1>
              <p className="text-slate-400 mt-1">Komunikacja w czasie rzeczywistym</p>
            </div>
            <div className="flex items-center gap-2">
              {wsConnected ? (
                <span className="flex items-center gap-1 text-green-400 text-sm">
                  <Wifi className="w-4 h-4" />
                  Połączono
                </span>
              ) : (
                <span className="flex items-center gap-1 text-red-400 text-sm">
                  <WifiOff className="w-4 h-4" />
                  Rozłączono
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[600px]">
          {/* Conversations List */}
          <Card className="md:col-span-1 overflow-hidden flex flex-col">
            <CardHeader className="border-b flex-shrink-0">
              <CardTitle className="font-heading text-lg">Konwersacje</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-y-auto flex-1">
              {conversations.length === 0 ? (
                <div className="p-6 text-center text-slate-500">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>Brak konwersacji</p>
                  <p className="text-sm mt-2">Rozpocznij rozmowę odpowiadając na ofertę lub kontaktując się z firmą.</p>
                </div>
              ) : (
                <div className="divide-y">
                  {conversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => selectConversation(conv)}
                      data-testid={`conversation-${conv.id}`}
                      className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${
                        selectedConversation?.id === conv.id ? 'bg-orange-50 border-l-4 border-orange-500' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                          {conv.other_user?.user_type === 'company' ? (
                            <Building2 className="w-5 h-5 text-slate-600" />
                          ) : (
                            <User className="w-5 h-5 text-slate-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-slate-900 truncate">
                              {conv.other_user?.name || 'Użytkownik'}
                            </p>
                            {conv.unread_count > 0 && (
                              <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full ml-2">
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 truncate">
                            {conv.last_message?.content || 'Rozpocznij rozmowę...'}
                          </p>
                          {conv.conversation_type === 'offer' && (
                            <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                              Czat z oferty
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Messages */}
          <Card className="md:col-span-2 flex flex-col overflow-hidden">
            {selectedConversation ? (
              <>
                <CardHeader className="border-b flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="md:hidden"
                      onClick={() => setSelectedConversation(null)}
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                      {selectedConversation.other_user?.user_type === 'company' ? (
                        <Building2 className="w-5 h-5 text-slate-600" />
                      ) : (
                        <User className="w-5 h-5 text-slate-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{selectedConversation.other_user?.name}</p>
                      <p className="text-xs text-slate-500">
                        {selectedConversation.other_user?.user_type === 'company' ? 'Firma' : 'Klient indywidualny'}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="messages-container">
                  {messages.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-slate-500">
                      <div className="text-center">
                        <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        <p>Rozpocznij rozmowę</p>
                      </div>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        data-testid={`message-${msg.id}`}
                        className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] p-3 rounded-lg ${
                            msg.sender_id === user?.user_id
                              ? 'bg-orange-500 text-white'
                              : 'bg-slate-100 text-slate-900'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <p
                            className={`text-xs mt-1 ${
                              msg.sender_id === user?.user_id ? 'text-orange-100' : 'text-slate-500'
                            }`}
                          >
                            {new Date(msg.created_at).toLocaleString('pl-PL')}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </CardContent>
                
                {/* Typing indicator */}
                {typingUser && (
                  <div className="px-4 py-2 text-sm text-slate-500 italic border-t bg-slate-50" data-testid="typing-indicator">
                    {typingUser} pisze...
                  </div>
                )}
                
                <div className="border-t p-4 flex gap-2">
                  <Textarea
                    value={newMessage}
                    onChange={handleMessageChange}
                    onKeyPress={handleKeyPress}
                    onBlur={() => sendTypingIndicator(false)}
                    placeholder="Napisz wiadomość..."
                    rows={2}
                    className="flex-1 resize-none"
                    data-testid="message-input"
                    disabled={!wsConnected}
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={sending || !newMessage.trim() || !wsConnected}
                    className="bg-orange-500 hover:bg-orange-600 self-end"
                    data-testid="send-message-btn"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <CardContent className="flex-1 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Wybierz konwersację</p>
                  <p className="text-sm mt-1">lub rozpocznij nową odpowiadając na ofertę</p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Chat;
